import { useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet-async';
import { passwordApi } from '../api/settingsApi';
import { Logo } from '../components/Logo';

export const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const token = params.get('token') ?? '';
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  const mutation = useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      passwordApi.reset(token, password),
    onSuccess: () => {
      enqueueSnackbar('Heslo bylo nastaveno. Můžete se přihlásit.', { variant: 'success' });
      navigate('/login');
    },
    onError: (e: any) =>
      enqueueSnackbar(e?.response?.data?.message ?? 'Token je neplatný nebo vypršel', { variant: 'error' }),
  });

  const passwordsMatch = newPassword === confirm;
  const valid = newPassword.length >= 8 && passwordsMatch;

  return (
    <>
      <Helmet>
        <title>Nové heslo | HasičiOnline.cz</title>
      </Helmet>
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', bgcolor: 'background.default' }}>
        <Container maxWidth="sm">
          <Stack spacing={3} alignItems="center">
            <Logo size={64} />
            <Card sx={{ width: '100%', boxShadow: 4 }}>
              <CardContent sx={{ p: 4 }}>
                <Stack spacing={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={700} gutterBottom>
                      Nastavení nového hesla
                    </Typography>
                    {!token && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        V odkazu chybí token. Otevřete prosím odkaz z e-mailu znovu.
                      </Alert>
                    )}
                  </Box>
                  <Box
                    component="form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (token && valid) mutation.mutate({ token, password: newPassword });
                    }}
                  >
                    <Stack spacing={2}>
                      <TextField
                        label="Nové heslo"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        helperText="Alespoň 8 znaků"
                        fullWidth
                        required
                        autoFocus
                      />
                      <TextField
                        label="Potvrzení hesla"
                        type="password"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        error={!!confirm && !passwordsMatch}
                        helperText={!!confirm && !passwordsMatch ? 'Hesla se neshodují' : ''}
                        fullWidth
                        required
                      />
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={!token || !valid || mutation.isPending}
                      >
                        {mutation.isPending ? 'Nastavuji…' : 'Nastavit heslo'}
                      </Button>
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
            <Button component={RouterLink} to="/login" variant="text">
              Zpět na přihlášení
            </Button>
          </Stack>
        </Container>
      </Box>
    </>
  );
};
